import { useEffect, useRef, useState, useCallback } from "react";
import api, { API, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { LongTextarea, NoteView } from "@/components/LongText";
import {
  Camera, FileText, Trash2, Star, UploadCloud, Loader2, Check, X, GripVertical,
  Download, AlertCircle, Pencil,
} from "lucide-react";
import DocLightbox from "@/components/DocLightbox";
import { postUpload, chiediRinomina, NomeDocumentoInline } from "@/components/ConflittoNome";
import { fileDaDrop } from "@/lib/codaCaricamenti";
import { BarraSelezione, SelezionaTutto, Casella, eliminaInBlocco, zipInBlocco, useSelezione } from "@/components/SelezioneFile";

const FOTO_CATS = [
  ["interni", "Interni"], ["esterni", "Esterni"], ["pertinenze", "Pertinenze"],
  ["criticita", "Criticità"], ["altro", "Altro"],
];
const DOC_TIPI = [
  ["incarico", "Incarico firmato"], ["documento_identita", "Documento proprietario"],
  ["codice_fiscale", "Codice fiscale"], ["privacy", "Privacy (firmata)"], ["delega", "Delega"],
  ["planimetria", "Planimetria catastale"], ["visura", "Visura catastale"], ["visura_ipotecaria", "Visura ipotecaria"],
  ["atto", "Provenienza (atto)"], ["ape", "APE"], ["condominiali", "Spese condominiali"], ["are", "ARE"],
  ["titoli_edilizi", "Documenti urbanistici"], ["descrizione", "Descrizione"], ["scheda_tecnica", "Scheda tecnica"], ["altri_allegati", "Altri allegati"],
  ["successione", "Successione"], ["locazione", "Contratto di locazione"],
  ["agibilita", "Agibilità / abitabilità"], ["altro", "Altro"],
];
const DOC_LABEL = Object.fromEntries(DOC_TIPI);
const FOTO_LABEL = Object.fromEntries(FOTO_CATS);

const fileUrl = (id) => `${API}/files/${id}/download`;
const fmtSize = (b) => (!b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1).replace(".", ",")} MB` : `${Math.round(b / 1024)} KB`);

function useFiles(entityType, entityId, kind) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    if (!entityId) return;
    setLoading(true);
    api.get(`/files`, { params: { entity_type: entityType, entity_id: entityId, kind } })
      .then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [entityType, entityId, kind]);
  useEffect(() => { load(); }, [load]);
  return { items, setItems, loading, reload: load };
}

async function uploadOne(f, { entityType, entityId, kind, extra }, onProgress) {
  const fd = new FormData();
  fd.append("file", f);
  fd.append("entity_type", entityType);
  fd.append("entity_id", entityId);
  fd.append("kind", kind);
  Object.entries(extra || {}).forEach(([k, v]) => v != null && fd.append(k, v));
  return postUpload(fd, {
    onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
  });
}

function DropZone({ accept, onFiles, label, testid, disabled }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <div
      data-testid={testid}
      onClick={() => !disabled && ref.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={async (e) => { e.preventDefault(); setDrag(false); if (!disabled) onFiles(await fileDaDrop(e.dataTransfer)); }}
      className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
        drag ? "border-[#FC82ED] bg-[#FC82ED]/5" : "border-[var(--border)] hover:border-[#FC82ED]/50"
      } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
    >
      <input ref={ref} type="file" accept={accept} multiple className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => { onFiles([...e.target.files]); e.target.value = ""; }} />
      <UploadCloud size={26} className="mx-auto text-[#FC82ED] mb-2" />
      <div className="text-sm font-semibold text-[#111111]">{label}</div>
      <div className="text-xs text-[var(--muted)] mt-0.5">Trascina qui i file o clicca per selezionare</div>
    </div>
  );
}

function UploadRow({ name, status, pct, error }) {
  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-[var(--border)]">
      {status === "uploading" && <Loader2 size={15} className="text-[#FC82ED] animate-spin shrink-0" />}
      {status === "done" && <Check size={15} className="text-emerald-600 shrink-0" />}
      {status === "error" && <AlertCircle size={15} className="text-red-500 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{name}</div>
        <div className={`text-[11px] ${status === "error" ? "text-red-500" : "text-[var(--muted)]"}`}>
          {status === "uploading" ? `Caricamento… ${pct}%` : status === "done" ? "Caricato" : (error || "Errore nel caricamento")}
        </div>
      </div>
    </div>
  );
}

/* ---------------- PHOTO MANAGER ---------------- */
export function PhotoManager({ entityType, entityId, readOnly }) {
  const { items, loading, reload } = useFiles(entityType, entityId, "foto");
  const S = useSelezione();
  const [queue, setQueue] = useState([]);
  const [drag, setDrag] = useState(null);
  const [preview, setPreview] = useState(null);

  const handleFiles = async (files) => {
    const lavori = [];
    for (const f of files) {
      const key = `${f.name}-${Date.now()}-${Math.random()}`;
      const okType = /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name);
      const okSize = f.size <= 10 * 1024 ** 3;
      if (!okType || !okSize) {
        setQueue((q) => [...q, { key, name: f.name, status: "error", error: !okType ? "Formato non supportato (JPG, PNG, HEIC, WEBP)" : "Troppo grande (max 10 GB)" }]);
        continue;
      }
      setQueue((q) => [...q, { key, name: f.name, status: "uploading", pct: 0 }]);
      lavori.push(async () => {
        try {
          await uploadOne(f, { entityType, entityId, kind: "foto", extra: { categoria: "altro", conflitto: files.length > 1 ? "entrambi" : null } },
            (pct) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, pct } : x))));
          setQueue((q) => q.map((x) => (x.key === key ? { ...x, status: "done" } : x)));
        } catch (e) {
          setQueue((q) => q.map((x) => (x.key === key ? { ...x, status: "error", error: (e?.response?.data?.detail) || "Errore nel caricamento" } : x)));
        }
      });
    }
    await Promise.all(Array.from({ length: 3 }, async () => { while (lavori.length) await lavori.shift()(); }));
    setTimeout(() => setQueue((q) => q.filter((x) => x.status === "error")), 3000);
    reload();
  };

  const patch = async (id, body) => { await api.patch(`/files/${id}`, body); reload(); };
  const del = async (id) => { await api.delete(`/files/${id}`); toast.success("Foto eliminata"); reload(); };
  const setCover = async (id) => { await api.patch(`/files/${id}`, { is_copertina: true }); toast.success("Impostata come copertina"); reload(); };

  const onDrop = async (targetId) => {
    if (!drag || drag === targetId) return;
    const ids = items.map((i) => i.id);
    const from = ids.indexOf(drag), to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDrag(null);
    await api.put(`/files/reorder`, { ids });
    reload();
  };

  return (
    <div data-testid="photo-manager">
      <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#FC82ED] mb-3">FOTO IMMOBILE</h3>
      {!readOnly && <DropZone accept="image/*" testid="foto-dropzone" label="Carica foto immobile" onFiles={handleFiles} />}
      {queue.length > 0 && <div className="space-y-1.5 mt-3">{queue.map(({ key, ...rest }) => <UploadRow key={key} {...rest} />)}</div>}

      {loading ? <div className="skeleton h-24 rounded-xl mt-4" /> : items.length === 0 ? (
        <div className="text-sm text-[var(--muted)] mt-4">Nessuna foto caricata.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
          <BarraSelezione sel={S.sel} onAnnulla={S.annulla} onElimina={readOnly ? null : () => eliminaInBlocco([...S.sel], reload).then((ok) => ok && S.annulla())} onZip={() => zipInBlocco([...S.sel])} />
          <div className="col-span-full -mb-1"><SelezionaTutto ids={items.map((x) => x.id)} sel={S.sel} onTutti={S.tutti} testid="foto-gallery-seltutto" /></div>
          {items.map((it) => (
            <div key={it.id} data-testid={`foto-card-${it.id}`}
              draggable={!readOnly}
              onDragStart={() => setDrag(it.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(it.id)}
              className={`relative rounded-xl overflow-hidden border group bg-white ${S.sel.has(it.id) ? "border-[#FC82ED] ring-2 ring-[#FC82ED]" : it.is_copertina ? "border-[#FC82ED] ring-2 ring-[#FC82ED]/30" : "border-[var(--border)]"}`}>
              <Casella on={S.sel.has(it.id)} onToggle={() => S.toggle(it.id)} testid={`foto-gallery-sel-${it.id}`} className="top-2 right-2" />
              <img src={`${fileUrl(it.id)}?thumb=1`} alt={it.descrizione || it.categoria} loading="lazy" onClick={() => setPreview(it)} className="w-full h-32 object-cover cursor-zoom-in" />
              {it.is_copertina && <span className="absolute top-1.5 left-1.5 text-[10px] font-bold uppercase bg-[#FC82ED] text-black px-1.5 py-0.5 rounded">Copertina</span>}
              {!readOnly && !it.is_copertina && <GripVertical size={14} className="absolute top-2 left-2 text-white/80 drop-shadow opacity-0 group-hover:opacity-100" />}
              <div className="p-2">
                {readOnly ? (
                  <div className="text-[11px] font-medium">{FOTO_LABEL[it.categoria] || "Altro"}</div>
                ) : (
                  <select data-testid={`foto-cat-${it.id}`} value={it.categoria || "altro"}
                    onChange={(e) => patch(it.id, { categoria: e.target.value })}
                    className="w-full text-[11px] rounded border border-[var(--border)] px-1 py-1 mb-1 outline-none focus:border-[#FC82ED]">
                    {FOTO_CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                )}
                {!readOnly && (
                  <LongTextarea data-testid={`foto-desc-${it.id}`} defaultValue={it.descrizione || ""} placeholder="Descrizione"
                    onBlur={(e) => e.target.value !== (it.descrizione || "") && patch(it.id, { descrizione: e.target.value })}
                    className="w-full text-[11px] rounded border border-[var(--border)] px-1.5 py-1 outline-none focus:border-[#FC82ED]" />
                )}
                {readOnly && it.descrizione && <NoteView text={it.descrizione} lines={2} className="text-[11px] text-[var(--muted)]" />}
              </div>
              {!readOnly && (
                <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!it.is_copertina && <button data-testid={`foto-cover-${it.id}`} onClick={() => setCover(it.id)} title="Imposta copertina" className="p-1 rounded bg-white/90 text-[#FC82ED] hover:bg-white"><Star size={13} /></button>}
                  <button data-testid={`foto-del-${it.id}`} onClick={() => del(it.id)} className="p-1 rounded bg-white/90 text-red-500 hover:bg-white"><Trash2 size={13} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {preview && <DocLightbox file={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

/* ---------------- DOCUMENT MANAGER ---------------- */
export function DocumentManager({ entityType, entityId, readOnly }) {
  const { items, loading, reload } = useFiles(entityType, entityId, "documento");
  const SD = useSelezione();
  const [queue, setQueue] = useState([]);
  const [tipo, setTipo] = useState("incarico");
  const [appena, setAppena] = useState(null);

  const handleFiles = async (files) => {
    const ok = files.filter((f) => f.size <= 10 * 1024 ** 3);
    if (ok.length !== files.length) toast.error("Alcuni file superano 10 GB");
    let ultimo = null;
    for (const f of ok) {
      const key = `${f.name}-${Date.now()}-${Math.random()}`;
      setQueue((q) => [...q, { key, name: f.name, status: "uploading", pct: 0 }]);
      try {
        ultimo = await uploadOne(f, { entityType, entityId, kind: "documento", extra: { doc_tipo: tipo, conflitto: ok.length > 1 ? "entrambi" : null } },
          (pct) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, pct } : x))));
        setQueue((q) => q.map((x) => (x.key === key ? { ...x, status: "done" } : x)));
      } catch (e) {
        toast.error(e.response?.data?.detail || `Errore nel caricamento di ${f.name}`);
        setQueue((q) => q.map((x) => (x.key === key ? { ...x, status: "error" } : x)));
      }
    }
    setTimeout(() => setQueue([]), 1500);
    if (ok.length === 1 && ultimo?.id) setAppena(ultimo.id);
    reload();
  };

  const patch = async (id, body) => { await api.patch(`/files/${id}`, body); reload(); };
  const del = async (id) => { await api.delete(`/files/${id}`); toast.success("Documento eliminato"); reload(); };

  return (
    <div data-testid="document-manager">
      <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#FC82ED] mb-3">DOCUMENTI</h3>
      {!readOnly && (
        <div className="mb-3">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Tipo documento da caricare</label>
          <select data-testid="doc-tipo-select" value={tipo} onChange={(e) => setTipo(e.target.value)}
            className="w-full sm:w-72 px-3 py-2 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm mb-3">
            {DOC_TIPI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <DropZone accept="" testid="doc-dropzone" label={`Carica: ${DOC_LABEL[tipo]}`} onFiles={handleFiles} />
          <div className="text-[11px] text-[var(--muted)] mt-1">Qualsiasi formato (PDF, Word, Excel, immagini, HEIC, ZIP, DWG, P7M…) fino a 10 GB.</div>
        </div>
      )}
      {queue.length > 0 && <div className="space-y-1.5 mt-3">{queue.map(({ key, ...rest }) => <UploadRow key={key} {...rest} />)}</div>}

      {loading ? <div className="skeleton h-24 rounded-xl mt-4" /> : items.length === 0 ? (
        <div className="text-sm text-[var(--muted)] mt-4">Nessun documento caricato.</div>
      ) : (
        <div className="space-y-2 mt-4">
          <BarraSelezione sel={SD.sel} onAnnulla={SD.annulla} onElimina={readOnly ? null : () => eliminaInBlocco([...SD.sel], reload).then((ok) => ok && SD.annulla())} onZip={() => zipInBlocco([...SD.sel])} />
          <SelezionaTutto ids={items.map((x) => x.id)} sel={SD.sel} onTutti={SD.tutti} testid="doc-seltutto" />
          {items.map((it) => (
            <div key={it.id} data-testid={`doc-row-${it.id}`} className={`relative flex items-center gap-3 p-3 pl-11 rounded-xl border bg-white ${SD.sel.has(it.id) ? "border-[#FC82ED] bg-[#FC82ED]/5" : "border-[var(--border)]"}`}>
              <Casella on={SD.sel.has(it.id)} onToggle={() => SD.toggle(it.id)} testid={`doc-sel-${it.id}`} className="top-1/2 -translate-y-1/2 left-3" />
              <FileText size={18} className="text-[#FC82ED] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase bg-[#FC82ED]/15 text-[#111111] px-1.5 py-0.5 rounded">{DOC_LABEL[it.doc_tipo] || "Altro"}</span>
                  <span className="text-sm font-medium truncate">{it.display_name || it.original_filename}</span>
                </div>
                {appena === it.id && !readOnly && <div className="mt-1.5"><NomeDocumentoInline valore={it.display_name || it.original_filename} testid={`nome-documento-${it.id}`} onSalva={async (n) => { try { await patch(it.id, { display_name: n }); toast.success("Nome aggiornato"); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } }} /></div>}
                <div className="text-[11px] text-[var(--muted)] mt-0.5 truncate">
                  {fmtSize(it.size)} · {it.uploaded_by_name} · {fmtDate(it.created_at)}
                  {it.scadenza ? ` · Scad. ${fmtDate(it.scadenza)}` : ""}
                </div>
              </div>
              <a href={fileUrl(it.id)} target="_blank" rel="noreferrer" data-testid={`doc-open-${it.id}`}
                className="p-1.5 rounded text-[#FC82ED] hover:bg-[#FC82ED]/10"><Download size={16} /></a>
              {!readOnly && (
                <button data-testid={`doc-rinomina-${it.id}`} title="Rinomina" onClick={async () => { const n = await chiediRinomina(it.display_name || it.original_filename); if (n && n.trim()) { try { await api.patch(`/files/${it.id}`, { display_name: n.trim() }); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } } }} className="p-1.5 rounded text-slate-500 hover:bg-slate-100"><Pencil size={15} /></button>
              )}
              {!readOnly && (
                <button data-testid={`doc-del-${it.id}`} onClick={() => del(it.id)} className="p-1.5 rounded text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
